import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { api, ApiError } from "@/api/client";

import { loadTopRanking } from "@/lib/ranking";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { loadMyGroups } from "@/lib/groups";
import { uploadAvatar, removeAvatar, MAX_AVATAR_BYTES } from "@/lib/avatar";
import { UserAvatar } from "@/components/UserAvatar";
import { GroupLogo } from "@/components/GroupLogo";
import { InviteButton } from "@/components/InviteButton";
import { Users, Plus, Camera, Trash2, BookOpen, Sparkles, FileText, Shield, Scale, Target, Pencil, Check as CheckIcon, X as XIcon } from "lucide-react";
import { BonusBanner } from "@/components/BonusBanner";
import { teamDe } from "@/lib/teamNames";
import { NotificationsCard } from "@/components/NotificationsCard";

export default function Profile() {
  const { user, profile, refreshProfile } = useAuth();
  const [tips, setTips] = useState<any[]>([]);
  const [games, setGames] = useState<any[]>([]);
  // badges removed
  const [myRank, setMyRank] = useState<number | null>(null);
  const [totalTippers, setTotalTippers] = useState(0);
  const [bonusPoints, setBonusPoints] = useState(0);
  const [myGroups, setMyGroups] = useState<any[]>([]);
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Username editor — opens inline under the avatar when the pencil is
  // clicked. Same 3-20 char + alphanum-and-underscore rule as onboarding,
  // plus live availability check via api.profiles.usernameAvailable.
  const [nameEditOpen, setNameEditOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameStatus, setNameStatus] = useState<"idle" | "checking" | "ok" | "taken" | "invalid">("idle");
  const [nameBusy, setNameBusy] = useState(false);

  const onAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;
    if (file.size > MAX_AVATAR_BYTES) return toast.error("Bild zu groß (max. 3 MB)");
    setAvatarBusy(true);
    try {
      await uploadAvatar(user.id, file);
      await refreshProfile();
      toast.success("Profilbild aktualisiert ✅");
    } catch (err: any) {
      toast.error(err.message ?? "Upload fehlgeschlagen");
    }
    setAvatarBusy(false);
  };

  const onRemoveAvatar = async () => {
    if (!user) return;
    setAvatarBusy(true);
    try { await removeAvatar(user.id); await refreshProfile(); toast.success("Profilbild entfernt"); }
    catch (err: any) { toast.error(err.message); }
    setAvatarBusy(false);
  };

  useEffect(() => { document.title = "Mein Profil – Englputzeder Tippspiel"; }, []);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.tips.listMine() as Promise<any[]>,
      api.games.list() as Promise<any[]>,
      // limit:0 → me + total only; the profile never lists other tippers.
      loadTopRanking(0),
      api.bonus.me().catch(() => null),
      loadMyGroups(user.id),
    ]).then(([t, g, top, bonus, groups]) => {
      setTips((t ?? []).map((x: any) => ({
        ...x, game_id: x.gameId, home_tip: x.homeTip, away_tip: x.awayTip, points_earned: x.pointsEarned,
      })));
      setGames((g ?? []).map((x: any) => ({
        ...x, home_team: x.homeTeam, away_team: x.awayTeam, home_flag: x.homeFlag, away_flag: x.awayFlag,
        home_score: x.homeScore, away_score: x.awayScore, is_finished: x.isFinished, kickoff_time: x.kickoffTime,
      })));
      setMyRank(top.me?.rank ?? null);
      setTotalTippers(top.total);
      setBonusPoints((bonus as any)?.pointsEarned ?? 0);
      setMyGroups(groups);
    });
  }, [user]);

  // Debounced username-availability check. Only runs while the editor is
  // open. Same rule as onboarding: 3-20 chars, [A-Za-z0-9_]. Skips the
  // API call when the value matches the current username (no-op save).
  useEffect(() => {
    if (!nameEditOpen) return;
    const v = nameDraft.trim();
    if (v === (profile?.username ?? "")) { setNameStatus("idle"); return; }
    if (!/^[A-Za-z0-9_]{3,20}$/.test(v)) {
      setNameStatus(v.length === 0 ? "idle" : "invalid");
      return;
    }
    setNameStatus("checking");
    const h = setTimeout(async () => {
      try {
        const r = await api.profiles.usernameAvailable(v);
        setNameStatus(r.available ? "ok" : "taken");
      } catch {
        setNameStatus("idle");
      }
    }, 400);
    return () => clearTimeout(h);
  }, [nameDraft, nameEditOpen, profile?.username]);

  const openNameEditor = () => {
    if (!profile) return;
    setNameDraft(profile.username);
    setNameStatus("idle");
    setNameEditOpen(true);
  };
  const cancelNameEdit = () => {
    setNameEditOpen(false);
    setNameDraft("");
    setNameStatus("idle");
  };
  const saveName = async () => {
    if (!profile) return;
    const v = nameDraft.trim();
    if (v === profile.username) { cancelNameEdit(); return; }
    if (!/^[A-Za-z0-9_]{3,20}$/.test(v)) return toast.error("3-20 Zeichen, nur Buchstaben, Zahlen, Unterstrich");
    if (nameStatus === "taken") return toast.error("Username bereits vergeben");
    setNameBusy(true);
    try {
      await api.profiles.update({ username: v });
      await refreshProfile();
      toast.success("Username aktualisiert ✅");
      cancelNameEdit();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Fehler";
      toast.error(msg.includes("username_taken") ? "Username bereits vergeben" : msg);
    } finally {
      setNameBusy(false);
    }
  };

  if (!profile) return null;
  const totalPoints = tips.reduce((s, t) => s + (t.points_earned ?? 0), 0) + bonusPoints;
  const exact = tips.filter(t => t.points_earned === 4).length;
  const tipByGame: Record<string, any> = {};
  tips.forEach(t => tipByGame[t.game_id] = t);

  const savePw = async () => {
    if (newPw.length < 8) return toast.error("Mindestens 8 Zeichen");
    if (newPw !== confirmPw) return toast.error("Passwörter stimmen nicht überein");
    setPwBusy(true);
    try {
      await api.auth.setPassword(newPw);
      toast.success("Passwort gespeichert ✅");
      setPwOpen(false);
      setNewPw("");
      setConfirmPw("");
      await refreshProfile();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-hero text-primary-foreground rounded-lg shadow-pop p-5 text-center">
        <div className="relative inline-block">
          <UserAvatar
            url={profile.avatarUrl}
            emoji={profile.emoji}
            size={112}
            className="ring-4 ring-primary-foreground/20 mx-auto"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={avatarBusy}
            className="absolute bottom-0 right-0 bg-primary-foreground text-primary rounded-full p-2 shadow-pop tap disabled:opacity-50"
            title="Profilbild hochladen"
            aria-label="Profilbild hochladen"
          >
            <Camera className="h-4 w-4" />
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatarFile} />
        </div>
        {nameEditOpen ? (
          <div className="mt-3 flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 w-full max-w-xs">
              <Input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value.replace(/[^A-Za-z0-9_]/g, "").slice(0, 20))}
                autoFocus
                maxLength={20}
                className="h-11 text-center font-bold text-base text-foreground"
              />
              <button
                onClick={saveName}
                disabled={nameBusy || nameStatus === "taken" || nameStatus === "invalid" || nameStatus === "checking" || nameDraft.trim() === profile.username}
                title="Speichern"
                className="bg-primary-foreground text-primary rounded-full p-2 shadow-pop tap disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4" />
              </button>
              <button
                onClick={cancelNameEdit}
                disabled={nameBusy}
                title="Abbrechen"
                className="bg-primary-foreground/20 text-primary-foreground rounded-full p-2 tap disabled:opacity-50"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="text-[11px] min-h-[1em]">
              {nameStatus === "checking" && <span className="text-primary-foreground/80">Prüfe Verfügbarkeit …</span>}
              {nameStatus === "ok" && <span className="text-emerald-200">✓ Verfügbar</span>}
              {nameStatus === "taken" && <span className="text-amber-200">❌ Bereits vergeben</span>}
              {nameStatus === "invalid" && <span className="text-amber-200">3-20 Zeichen, nur A-Z, 0-9, _</span>}
            </div>
          </div>
        ) : (
          <div className="mt-3 inline-flex items-center gap-2">
            <h1 className="font-display font-extrabold text-2xl">{profile.username}</h1>
            <button
              onClick={openNameEditor}
              title="Username ändern"
              aria-label="Username ändern"
              className="bg-primary-foreground/20 hover:bg-primary-foreground/30 text-primary-foreground rounded-full p-1.5 tap"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {profile.avatarUrl && (
          <button
            onClick={onRemoveAvatar}
            disabled={avatarBusy}
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary-foreground/80 underline tap"
          >
            <Trash2 className="h-3 w-3" /> Bild entfernen
          </button>
        )}
      </div>

      {/* Bonus-Fragen prominent */}
      <BonusBanner variant="hero" />

      {/* Push notifications opt-in */}
      <NotificationsCard />

      {/* Invite friends */}
      <InviteButton variant="cta" label="Freunde per WhatsApp einladen 📲" />

      {/* Account */}
      <div className="bg-card rounded-lg shadow-card p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Account</div>
            <div className="font-display font-bold text-base">{profile.hasPassword ? "Passwort gesetzt 🔐" : "Kein Passwort hinterlegt"}</div>
          </div>
          <Button onClick={() => setPwOpen(v => !v)} className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
            {profile.hasPassword ? "Ändern" : "Setzen 🔐"}
          </Button>
        </div>
        {pwOpen && (
          <div className="mt-3 space-y-2">
            <Input type="password" placeholder="Neues Passwort (min. 6 Zeichen)" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="h-11" />
            <Input type="password" placeholder="Passwort bestätigen" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="h-11" />
            <Button onClick={savePw} disabled={pwBusy} className="w-full h-11 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
              {pwBusy ? "Speichere …" : "Speichern"}
            </Button>
          </div>
        )}
      </div>

      {/* Hilfe / Tour */}
      <div className="bg-card rounded-lg shadow-card p-4 space-y-2">
        <Link to="/bonus" className="flex items-center gap-3 tap hover:bg-muted -mx-2 px-2 py-2 rounded-md transition">
          <Target className="h-5 w-5 text-accent-foreground bg-accent rounded-md p-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold leading-tight">🎯 Deine Bonus-Fragen</div>
            <div className="text-[11px] text-muted-foreground">WM-Sieger, Torschützenkönig & Gruppensieger tippen</div>
          </div>
          <span className="text-xs text-muted-foreground">→</span>
        </Link>
        <Link to="/regeln" className="flex items-center gap-3 tap hover:bg-muted -mx-2 px-2 py-2 rounded-md transition">
          <BookOpen className="h-5 w-5 text-accent-foreground bg-accent rounded-md p-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold leading-tight">📖 Spielregeln</div>
            <div className="text-[11px] text-muted-foreground">Punktesystem, Bonus-Tipps & alle Regeln</div>
          </div>
          <span className="text-xs text-muted-foreground">→</span>
        </Link>
        <button
          onClick={async () => {
            if (!user) return;
            // Reset the tour flag only — onboardingCompleted stays true so
            // the user is not bounced back into the full signup wizard.
            await api.profiles.update({ tourSeen: false });
            await refreshProfile();
            toast.success("Tour wird beim nächsten Start angezeigt 🎯");
          }}
          className="w-full flex items-center gap-3 tap hover:bg-muted -mx-2 px-2 py-2 rounded-md transition text-left"
        >
          <Sparkles className="h-5 w-5 text-accent-foreground bg-accent rounded-md p-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold leading-tight">App-Tour wiederholen</div>
            <div className="text-[11px] text-muted-foreground">Zeigt die Einführung beim nächsten Öffnen erneut</div>
          </div>
        </button>
      </div>

      {/* Rechtliches */}
      <div className="bg-card rounded-lg shadow-card p-4 space-y-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Rechtliches</div>
        <Link to="/impressum" className="flex items-center gap-3 tap hover:bg-muted -mx-2 px-2 py-2 rounded-md transition">
          <FileText className="h-5 w-5 text-accent-foreground bg-accent rounded-md p-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold leading-tight">Impressum</div>
            <div className="text-[11px] text-muted-foreground">Anbieter & Unternehmensdaten</div>
          </div>
          <span className="text-xs text-muted-foreground">→</span>
        </Link>
        <Link to="/datenschutz" className="flex items-center gap-3 tap hover:bg-muted -mx-2 px-2 py-2 rounded-md transition">
          <Shield className="h-5 w-5 text-accent-foreground bg-accent rounded-md p-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold leading-tight">Datenschutz</div>
            <div className="text-[11px] text-muted-foreground">Wie wir mit deinen Daten umgehen (DSGVO)</div>
          </div>
          <span className="text-xs text-muted-foreground">→</span>
        </Link>
        <Link to="/teilnahmebedingungen" className="flex items-center gap-3 tap hover:bg-muted -mx-2 px-2 py-2 rounded-md transition">
          <Scale className="h-5 w-5 text-accent-foreground bg-accent rounded-md p-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold leading-tight">Teilnahmebedingungen</div>
            <div className="text-[11px] text-muted-foreground">Geschicklichkeitsspiel · Preise · Voraussetzungen</div>
          </div>
          <span className="text-xs text-muted-foreground">→</span>
        </Link>
      </div>

      {/* My groups */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><Users className="h-5 w-5" /> Meine Tippgruppen</h2>
          <Link to="/gruppen" className="text-xs font-bold text-accent-foreground bg-accent rounded-pill px-3 py-1 tap">+ Gruppe</Link>
        </div>
        {myGroups.length === 0 ? (
          <Link to="/gruppen" className="block bg-card rounded-lg shadow-card p-4 text-center text-sm text-muted-foreground tap">
            <Plus className="h-5 w-5 mx-auto mb-1 opacity-60" />
            Du bist in keiner Gruppe — jetzt beitreten oder selbst gründen
          </Link>
        ) : (
          <div className="space-y-2">
            {myGroups.map(g => (
              <div key={g.id} className="bg-card rounded-lg shadow-card p-3 flex items-center gap-3">
                <Link to={`/gruppe/${g.slug}`} className="flex items-center gap-3 flex-1 min-w-0 tap">
                  <GroupLogo url={g.logo_url} size={48} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{g.name}</div>
                    <div className="text-[11px] text-muted-foreground">{g.member_count} Mitglieder · {g.my_role === "admin" ? "👑 Admin" : "Mitglied"}</div>
                  </div>
                </Link>
                <InviteButton variant="icon" groupName={g.name} code={g.invite_code} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Persönlicher Stand (kein Preisbezug — Preise gehen an Gruppen) */}
      {myRank && (
        <div className="rounded-lg shadow-card p-4 bg-card border border-border">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Deine Einzelplatzierung</div>
          <div className="font-display font-bold text-base mt-1">
            Rang #{myRank} von {totalTippers}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            💡 Preise gehen an die punktstärksten <b>Tippgruppen</b> — deine Punkte zählen für deine Gruppe.
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <Stat label="Punkte" value={totalPoints} accent />
        <Stat label="Tipps" value={tips.length} />
        <Stat label="Exakt" value={exact} />
        <Stat label="Bonus" value={bonusPoints} />
      </div>

      {/* Bonus pick — see BonusBanner above */}

      {/* Badges entfernt */}

      {/* Tip history */}
      <section>
        <h2 className="font-display font-bold text-lg mb-2">Meine Tipps</h2>
        <div className="bg-card rounded-lg shadow-card divide-y divide-border">
          {games.map(g => {
            const t = tipByGame[g.id];
            return (
              <div key={g.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span>{g.home_flag}{g.away_flag}</span>
                  <span className="font-semibold">{teamDe(g.home_team).slice(0,3)} vs {teamDe(g.away_team).slice(0,3)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {g.is_finished && g.home_score != null && (
                    <span className="text-xs text-muted-foreground tabular-nums">{g.home_score}:{g.away_score}</span>
                  )}
                  {t ? <span className="font-bold tabular-nums bg-muted rounded-md px-2 py-0.5">{t.home_tip}:{t.away_tip}</span> : <span className="text-xs italic text-muted-foreground">—</span>}
                  {t && g.is_finished && <span className="text-xs font-bold w-8 text-right">+{t.points_earned}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: any; accent?: boolean }) {
  return (
    <div className={`rounded-lg p-2.5 shadow-card text-center ${accent ? "bg-accent text-accent-foreground" : "bg-card"}`}>
      <div className="text-[9px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-display font-extrabold text-base tabular-nums">{value}</div>
    </div>
  );
}
